import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Loader2, Music } from 'lucide-react';

const BACKGROUND_IMAGE = '/assets/migrated/background.png';
const LOGO_IMAGE = '/assets/brand/logo-inline.png';

const MOODS = ['Upbeat', 'Chill', 'Emotional', 'Energetic', 'Romantic', 'Melancholic', 'Fun', 'Epic'];
const GENRES = ['Pop', 'Rock', 'Country', 'Hip Hop', 'R&B', 'Folk', 'Electronic', 'Jazz'];

/** Small helper — generates or retrieves the visitor's persistent demo
 *  token. Backend uses this to bind the demo to the eventual signup. */
const getOrCreateVisitorToken = () => {
  try {
    let t = localStorage.getItem('wim_demo_token');
    if (!t) {
      t = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('wim_demo_token', t);
    }
    return t;
  } catch {
    return 'v_' + Date.now().toString(36);
  }
};

export default function DemoCreate() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [mood, setMood] = useState('Upbeat');
  const [genre, setGenre] = useState('Pop');
  const [voice, setVoice] = useState('female');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [quota, setQuota] = useState({ ip_can_create: true, global_remaining: 100 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiFetch('/demo/quota');
        setQuota(data);
      } catch { /* soft-fail: allow the user to try anyway */ }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || prompt.trim().length < 3) {
      toast.error('Tell us a little about your song (at least a few words).');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const visitor_token = getOrCreateVisitorToken();
      const { data } = await apiFetch('/demo/songs', {
        method: 'POST',
        body: JSON.stringify({
          visitor_token,
          title: title.trim() || undefined,
          prompt: prompt.trim(),
          mood: mood.toLowerCase(),
          genre: genre.toLowerCase(),
          voice,
          website, // honeypot — expected to stay empty
        }),
      });
      toast.success('Building your demo — sit tight, this takes ~1 minute.');
      navigate(`/demo/song/${data.demo_id}`);
    } catch (err) {
      toast.error(err?.message || 'Could not start your demo. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const blocked = quota && !quota.ip_can_create;

  return (
    <div
      className="min-h-screen text-white relative"
      style={{
        backgroundImage: `url('${BACKGROUND_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
      data-testid="demo-create-page"
    >
      <div className="absolute inset-0 bg-slate-950/60" />
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/')}
          className="text-white/60 hover:text-white text-sm mb-6 inline-flex items-center gap-2"
          data-testid="demo-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="text-center mb-8">
          <img src={LOGO_IMAGE} alt="Wrapped In Music" className="h-10 md:h-12 mx-auto mb-6" />
          <h1 className="text-2xl md:text-4xl font-light mb-3">Try one full song, free — no signup.</h1>
          <p className="text-white/70">
            Describe your song idea and pick a style. We&apos;ll build the full track and let you
            decide if you want to sign up after you hear it.
          </p>
        </div>

        {blocked && (
          <div className="mb-6 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm" data-testid="demo-limit-banner">
            You&apos;ve already made your free demo today. <button className="underline font-medium" onClick={() => navigate('/auth?mode=signup')} data-testid="demo-limit-signup-link">Sign up</button> to make unlimited songs.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-md">
          <div>
            <Label className="text-white/80 mb-2 block">Give your song a title (optional)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Sunday Morning Coffee"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="demo-title-input"
              disabled={blocked || submitting}
            />
          </div>

          <div>
            <Label className="text-white/80 mb-2 block">What&apos;s your song about?</Label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="A road trip with my best friend, driving through the mountains at sunset..."
              className="w-full bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/40 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="demo-prompt-input"
              disabled={blocked || submitting}
            />
            <div className="text-right text-xs text-white/40 mt-1">{prompt.length}/500</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80 mb-2 block">Mood</Label>
              <select
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-md text-white p-2.5 text-sm"
                data-testid="demo-mood-select"
                disabled={blocked || submitting}
              >
                {MOODS.map((m) => <option key={m} value={m} className="text-black">{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Genre</Label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-md text-white p-2.5 text-sm"
                data-testid="demo-genre-select"
                disabled={blocked || submitting}
              >
                {GENRES.map((g) => <option key={g} value={g} className="text-black">{g}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-white/80 mb-2 block">Vocals</Label>
            <div className="flex gap-3">
              {['female', 'male'].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVoice(v)}
                  disabled={blocked || submitting}
                  className={`flex-1 py-2.5 rounded-md border text-sm transition-all ${
                    voice === v
                      ? 'bg-purple-500/30 border-purple-400 text-white'
                      : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                  data-testid={`demo-voice-${v}`}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Honeypot — hidden from humans via inline style + aria/tabIndex.
              CSS `display:none` still lets bots submit the field, so we
              keep it renderable but positioned off-screen. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          />

          <Button
            type="submit"
            disabled={blocked || submitting}
            className="w-full py-6 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="demo-submit-btn"
          >
            {submitting
              ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Starting your demo…</>
              : <><Sparkles className="mr-2 h-5 w-5" /> Create my demo song</>}
          </Button>

          <p className="text-center text-white/50 text-xs">
            <Music className="inline h-3 w-3 mr-1" />
            One full song, ~1 minute to generate. Powered by MusicGPT.
          </p>
        </form>
      </div>
    </div>
  );
}
